import axios from 'axios';

// LULA Components
import Datepicker from '@vuepic/vue-datepicker';
import Typeahead from 'vue3-simple-typeahead';
import FloatingVue from 'floating-vue';
import GoogleAutocomplete from './components/GoogleAutocomplete.vue';
import StateSelect from './components/StateSelect.vue';
import Loader from './components/Loader.vue';
import Modal from './components/Modal.vue';
import Toggle from './components/Toggle.vue';
import '@vuepic/vue-datepicker/dist/main.css';
import 'vue3-simple-typeahead/dist/vue3-simple-typeahead.css';
import 'floating-vue/dist/style.css';

/**
 * @callback frontendMount
 * @param {HTMLElement | string} selector - An element or selector to mount frontend to
 * @param {Object} params - Properties that can be passed into the frontend
 */

/**
 * Set global fallback subdomain for localhost microfrontends
 * @param {string} [subdomain="paddocks"] - Fallback subdomain
 */
export function setFrontendFallbackSubdomain(subdomain = 'paddocks') {
  // eslint-disable-next-line
  window['_lula_fallback_mfe_subdomain'] = subdomain;
}

/**
 * Add microfrontend to the page
 * @param {string} key - key to associate the microfrontend mount function with
 * @param {frontendMount} mount - function used to mount the microfrontend
 */
export function addFrontend(key, mount) {
  /* eslint-disable */
  if (window['_lula_mounts']) {
    window['_lula_mounts'][key] = mount;
  } else {
    window['_lula_mounts'] = { [key]: mount };
  }
  /* eslint-enable */
}

/**
 * Mount a microfrontend
 * @param {string} key - key of the microfrontend to mount
 * @param {HTMLElement | string} selector - Element to mount the microfrontend to
 * @prams {Object} params - Properties that can be passed into the microfrontend
 */
export function mountFrontend(key, selector, params) {
  /* eslint-disable */
  if (key in (window['_lula_mounts'] || {})) {
    window['_lula_mounts'][key](selector, params);
  } else {
    throw new Error(`${key} frontend mount not recognized.`);
  }
  /* eslint-enable */
}

/**
 * Fetch assets for a microfrontend
 * @param {string} context - Subcontext to fetch microfrotnend assets from
 * @param {HTMLElement} shadow - Shadow DOM enabled element to fetch assets from
 * @param {string} [override] - Override path to fetch assets from
 * @returns {Promise<Object>} - Promise that resolves with the manifest response
 */
export function fetchFrontend(shadow, serviceUrl) {
  // eslint-disable-next-line
  return new Promise(async (resolve) => {
    const manifest = await axios({
      method: 'GET',
      url: `${serviceUrl}/manifest.json?v=${new Date().getTime()}`,
    });
    Object.keys(manifest.data).forEach((asset) => {
      if (asset.includes('.js')) {
        const script = document.createElement('script');
        script.setAttribute('type', 'text/javascript');
        script.setAttribute('src', `${serviceUrl}${manifest.data[asset]}`);
        if (asset === 'app.js') {
          script.addEventListener('load', () => resolve(manifest));
        }
        shadow.append(script);
      } else {
        const link = document.createElement('link');
        link.setAttribute('rel', 'stylesheet');
        link.setAttribute('href', `${serviceUrl}${manifest.data[asset]}`);
        shadow.append(link);
      }
    });
    const observer = new MutationObserver(() => {
      const images = shadow.querySelectorAll('img');
      images.forEach((img) => {
        try {
          const servicePath = new URL(serviceUrl);
          const imagePath = new URL(img.getAttribute('src'));
          if (imagePath.host !== servicePath.host) {
            img.setAttribute('src', `${serviceUrl}${img.getAttribute('src')}`);
          }
        } catch (err) {
          img.setAttribute('src', `${serviceUrl}${img.getAttribute('src')}`);
        }
      });
    });
    observer.observe(shadow, {
      attributes: false,
      childList: true,
      subtree: true,
    });
  });
}

/**
 * Initialize a shadow DOM with a root element
 * @params {HTMLElement} element - Element to initialize shadow DOM with root element
 * @returns {Array<HTMLElement>} - An array with the shadow element and root node
 */
export function initializeShadowDom(element) {
  element.attachShadow({ mode: 'open' });
  const container = document.createElement('div');
  const shadow = element.shadowRoot;
  shadow.append(container);
  return [shadow, container];
}

export {
  StateSelect,
  Loader,
  Modal,
  Toggle,
  Datepicker,
  Typeahead,
  GoogleAutocomplete,
  FloatingVue,
};
